<template>
  <div class="min-h-screen bg-gray-50 px-4 pb-20">
    <div class="max-w-2xl mx-auto py-10">
      <!-- Main Card -->
      <div
        class="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden"
        style="box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);"
      >
        <div class="p-8 md:p-10">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-10">
            <Loader2
              :size="48"
              class="animate-spin text-primary-600 mx-auto mb-4"
            />
            <p class="text-gray-600 font-medium">Processing your order...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="text-center space-y-4">
            <div class="bg-red-50 border border-red-200 rounded-2xl p-6">
              <AlertCircle :size="48" class="text-red-600 mx-auto mb-4" />
              <h5 class="text-lg font-bold text-gray-900 mb-2">
                {{ storeAlias }} - Order #{{ orderId }}
              </h5>
              <h2 class="text-2xl font-bold text-primary-600 mb-4">
                You're Here—We're On Our Way!
              </h2>
              <p class="text-red-600 font-medium">
                {{ error }}
              </p>
            </div>
          </div>

          <!-- Success State -->
          <div v-else-if="submitted" class="text-center space-y-4">
            <div class="bg-green-50 border border-green-200 rounded-2xl p-6">
              <CheckCircle :size="48" class="text-green-600 mx-auto mb-4" />
              <h5 class="text-lg font-bold text-gray-900 mb-2">
                {{ storeAlias }} - Order #{{ orderId }}
              </h5>
              <h2 class="text-2xl font-bold text-primary-600 mb-4">
                You're Here—We're On Our Way!
              </h2>
              <p class="text-gray-700">
                Thank you for letting us know you've arrived! Our team has been
                notified and will bring your order out shortly.
              </p>
            </div>
          </div>

          <!-- Form State -->
          <div v-else class="space-y-6">
            <div class="text-center mb-6">
              <h5 class="text-lg font-bold text-gray-900 mb-2">
                {{ storeAlias }} - Order #{{ orderId }}
              </h5>
              <h2 class="text-2xl font-bold text-primary-600 mb-2">
                Let us know you're here!
              </h2>
            </div>

            <form @submit.prevent="submitPickup" class="space-y-6">
              <Input
                label="Car Model & Color"
                v-model="carModel"
                placeholder="Enter your car model and color"
                required
              />

              <Input
                label="Parking Number/Spot"
                v-model="parkingNumber"
                placeholder="Enter your parking spot number"
                required
              />

              <Button
                type="submit"
                variant="primary"
                :loading="submitting"
                :disabled="submitting"
                full-width
                custom-class="w-full font-bold py-4 rounded-xl shadow-lg hover:bg-primary-700 transition-all"
                size="lg"
              >
                {{ submitting ? "Submitting..." : "I'm Here - Notify Staff" }}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Input, Button } from "@/components/ui";
import { useOrderPickup } from "@/composables/useOrderPickup";
import { Loader2, AlertCircle, CheckCircle } from "lucide-vue";

export default {
  name: "OrderPickup",
  components: {
    Input,
    Button,
    Loader2,
    AlertCircle,
    CheckCircle
  },
  data() {
    return {
      loading: true,
      error: null,
      storeAlias: null,
      orderId: null,
      carModel: "",
      parkingNumber: "",
      submitting: false,
      submitted: false,
      token: null
    };
  },
  created() {
    // Initialize composable with component state
    this.orderPickupComposable = useOrderPickup(
      this.$store,
      this.$router,
      this.$route,
      this // Pass component instance so composable can access reactive state
    );
  },
  async mounted() {
    try {
      await this.orderPickupComposable.initialize();
    } catch (err) {
      // Error is already set in componentState by the composable
      console.error("Failed to initialize order pickup:", err);
    }
  },
  methods: {
    async submitPickup() {
      try {
        await this.orderPickupComposable.submitPickup();
      } catch (err) {
        // Error is already set in componentState by the composable
        console.error("Failed to submit pickup:", err);
      }
    }
  }
};
</script>
